/**
 * Zone capture ("assault"). Replaces the hard-coded server snapshots of the offline build (net_get_assault /
 * net_proceed_assault, FWP.as:14949-15023).
 *
 * Zone state: `stage` (= zone_stage, number of captures), owner, lastCaptureAt. A capture increments the stage
 * and records the capturer's team snapshot as the zone defender.
 *
 * A zone belongs to a PLAYER, as in the original. A clan (services/clans.ts) holds the zones of its current members:
 * the owner's clan (`ownerClanId`, `ownerClan`) is read from the clan directory whenever a zone is read (the stored
 * value is only refreshed), so a member who leaves takes its zones out of the clan. The clan's zones cannot be attacked
 * by its members ('clan'), and the members get a tenth of each other's zones' tribute. After every stored capture
 * the listeners of `onCapture` learn of it (the clan history records it; ClanService.recordCapture), and after every
 * stored change of an owner (a capture, an operator's reset) those of `onOwnerChange` (the clans' zone counts).
 *
 * With the server-owned game state (trust level L2, design D4 / D7) the battle service asks this service for the
 * facts of an assault (zoneFacts at battle.start), captures a zone after a won battle (captureAfterBattle, idempotent
 * per battle) and hands the tribute inputs (tributeFacts) to the tribute.claim command, which credits the gold into
 * the canonical state. The tribute cooldown then lives only in the state (player.next_assault_payment); the old
 * `assault.claimTribute` request (computes the tribute without crediting it, with the account's own cooldown
 * AccountRecord.nextTributeAt) is kept for the client-owned levels L0/L1 and refused at L2. So is the old
 * `assault.report` (a capture on the client's word, the defender from the client's team snapshot): at L2 a zone is
 * captured only by a won assault battle (battle.finish), with the defender taken from the canonical state.
 * // UNVERIFIED: the original server-side capture rules are not in the client; this follows
 * // docs/analysis/backend-requirements.md §4.2 (expectedStage, no capturing own/clan zones).
 */
import { BackendError } from '../../../shared/protocol/errors';
import type { AssaultTarget, AssaultZone, PlayerId, TeamSnapshot, TeamSnapshotInput, TributeResult } from '../../../shared/protocol/model';
import type { ZoneOwner } from '../../../game/rules/ops/battle';
import { ASSAULT_ZONES, type TributeZone } from '../../../game/rules/ops/progress-claims';
import type { ConnectionContext } from '../hub';
import { type StorageBatchOp, putOp, skipDamaged } from '../storage';
import type { AuthService } from './auth';
import type { ClanDirectory } from './clan-directory';
import { type AccountRecord, type ServiceDeps, C } from './deps';
import type { ProfileService } from './profile';

interface ZoneRecord {
  zone: AssaultZone;
  defender: TeamSnapshot | null;
  /** Recent attack log (bounded). */
  history: { at: number; playerId: PlayerId; outcome: 'win' | 'loss'; stage: number; battleId?: string; blocked?: CaptureBlocked | null }[];
}

/** Why a won battle did not capture its zone (captureAfterBattle). */
export type CaptureBlocked = 'own' | 'clan' | 'stage_changed';

/** The assault inputs of the tribute.claim rule op (src/game/rules/ops/progress-claims.ts TributeClaimArgs). */
export interface TributeFacts {
  /** Every zone with its owner and the last capture (unix seconds). */
  zones: TributeZone[];
  /** Owners of zones who are members of the player's clan (the only clan members the formula looks at). */
  clanMembers: PlayerId[];
}

/** A zone somebody owns now (the clan cards list these for their members). */
export interface HeldZone {
  zone: number;
  ownerId: PlayerId;
  ownerName: string;
  stage: number;
  lastCaptureAt: number;
  tributePerHour: number;
}

/** A capture that was just stored (the listeners of AssaultService.onCapture). */
export interface CaptureNotice {
  playerId: PlayerId;
  zone: number;
  /** The zone's stage after the capture. */
  stage: number;
  /** Who held the zone before (null = nobody). */
  previousOwnerId: PlayerId | null;
  at: number;
}

const HISTORY_KEPT = 50;

/** Mutex key of a zone's read-modify-write (captures, reports, resets, the account deletion). */
export function zoneLockKey(zone: number): string {
  return `zone:${zone}`;
}

/** Outcome of AssaultService.captureAfterBattle. */
export interface CaptureOutcome {
  captured: boolean;
  blocked: CaptureBlocked | null;
  zone: AssaultZone;
}

/**
 * Stores the documents of a capture (the zone, and the capturer's account on a capture) instead of the assault
 * service: the battle command writes them in ONE batch with the player's profile and the replay. Must write `ops`
 * (or throw: then nothing of the capture happened).
 */
export type CaptureCommit = (ops: StorageBatchOp[], outcome: CaptureOutcome) => Promise<void>;

/** The account write of a capture (AuthService.saveAccount semantics: updatedAt = now). */
function accountWrite(a: AccountRecord, now: number): StorageBatchOp {
  a.updatedAt = now;
  return putOp(C.accounts, a.playerId, a);
}

/** A zone nobody holds (never captured, reset by an operator, released by an account deletion). */
function emptyZone(zone: number): AssaultZone {
  return { zone, stage: 0, ownerId: null, ownerName: null, ownerClanId: null, ownerClan: null, lastCaptureAt: 0 };
}

/** The write of a zone record: the owner's clan brief is not stored (it is read from the clan directory). */
function zoneWrite(rec: ZoneRecord): StorageBatchOp {
  const { ownerClan: _brief, ...zone } = rec.zone;
  return putOp(C.zones, String(rec.zone.zone), { ...rec, zone });
}

/** Lookup of finished battles, provided by the SessionService (kept as an interface to avoid a cycle). */
export interface BattleOutcomeLookup {
  outcomeFor(sessionId: string): Promise<{
    playerIds: PlayerId[];
    teamsOf: (playerId: PlayerId) => number[];
    mode: string;
    zone: number | null;
    winnerTeam: number;
    verified: boolean;
  } | null>;
}

export class AssaultService {
  private readonly captureListeners = new Set<(c: CaptureNotice) => Promise<void> | void>();
  private readonly ownerListeners = new Set<(zone: number) => void>();

  constructor(
    private readonly d: ServiceDeps,
    private readonly auth: AuthService,
    private readonly profiles: ProfileService,
    private readonly battles: BattleOutcomeLookup,
    private readonly clans: ClanDirectory,
  ) {}

  /** Called after every stored capture (awaited, while the zone is still locked; a failing listener is logged). */
  onCapture(listener: (c: CaptureNotice) => Promise<void> | void): () => void {
    this.captureListeners.add(listener);
    return () => this.captureListeners.delete(listener);
  }

  /**
   * Called synchronously right after every stored change of a zone's owner: a capture (before the onCapture listeners)
   * and an operator's reset. The zones an account deletion releases are written by the deletion's own batch
   * (services/account-deletion.ts), which also takes the player out of its clan (ClanService.deletionStored).
   */
  onOwnerChange(listener: (zone: number) => void): () => void {
    this.ownerListeners.add(listener);
    return () => this.ownerListeners.delete(listener);
  }

  private ownerChanged(zone: number): void {
    for (const l of [...this.ownerListeners]) {
      try {
        l(zone);
      } catch (err) {
        this.d.logger.error('zone owner listener failed', { zone, err });
      }
    }
  }

  private async captured(notice: CaptureNotice): Promise<void> {
    this.ownerChanged(notice.zone);
    for (const l of [...this.captureListeners]) {
      try {
        await l(notice);
      } catch (err) {
        this.d.logger.error('capture listener failed', { zone: notice.zone, err });
      }
    }
  }

  private get zoneCount(): number {
    return this.d.config.assault.zoneCount;
  }

  private async zoneRecord(zone: number): Promise<ZoneRecord> {
    const rec = await this.d.storage.get<ZoneRecord>(C.zones, String(zone));
    return rec ?? { zone: emptyZone(zone), defender: null, history: [] };
  }

  private assertZone(zone: number): void {
    if (!Number.isInteger(zone) || zone < 1 || zone > this.zoneCount) throw new BackendError('NOT_FOUND', `Zone ${zone} does not exist`, { reason: 'zone_not_found', zone });
  }

  /** Refreshes the owner's name (from the account) and clan (from the clan directory): both change after a capture. */
  private async fresh(rec: ZoneRecord): Promise<ZoneRecord> {
    await this.clans.ready();
    if (rec.zone.ownerId) {
      // A damaged owner account keeps the stored name (it must not fail the zone list of everybody).
      const ownerId = rec.zone.ownerId;
      const owner = await skipDamaged(() => this.auth.getAccount(ownerId), this.d.logger, { playerId: ownerId });
      if (owner) rec.zone.ownerName = owner.displayName;
    }
    rec.zone.ownerClanId = this.clans.clanIdOf(rec.zone.ownerId);
    rec.zone.ownerClan = this.clans.brief(rec.zone.ownerClanId);
    return rec;
  }

  async getZones(): Promise<AssaultZone[]> {
    const out: AssaultZone[] = [];
    for (let z = 1; z <= this.zoneCount; z++) out.push((await this.fresh(await this.zoneRecord(z))).zone);
    return out;
  }

  /** Every zone somebody holds now, with the owner's current name and the zone's hourly tribute. */
  async heldZones(): Promise<HeldZone[]> {
    const out: HeldZone[] = [];
    for (let z = 1; z <= this.zoneCount; z++) {
      const { zone } = await this.fresh(await this.zoneRecord(z));
      if (!zone.ownerId) continue;
      out.push({ zone: z, ownerId: zone.ownerId, ownerName: zone.ownerName ?? '', stage: zone.stage, lastCaptureAt: zone.lastCaptureAt, tributePerHour: this.d.config.assault.payments[z - 1] ?? 0 });
    }
    return out;
  }

  /** The owner of every zone somebody holds (zone -> player): the zone documents only, no account or name. */
  async zoneOwners(): Promise<Map<number, PlayerId>> {
    const out = new Map<number, PlayerId>();
    for (let z = 1; z <= this.zoneCount; z++) {
      const ownerId = (await this.zoneRecord(z)).zone.ownerId;
      if (ownerId) out.set(z, ownerId);
    }
    return out;
  }

  /**
   * Operator API (admin console): every zone with its defender snapshot and its recent attacks (newest last), the
   * owner's name and clan refreshed.
   */
  async zoneRecordsForOperator(): Promise<{ zone: AssaultZone; defender: TeamSnapshot | null; history: ZoneRecord['history'] }[]> {
    const out: { zone: AssaultZone; defender: TeamSnapshot | null; history: ZoneRecord['history'] }[] = [];
    for (let z = 1; z <= this.zoneCount; z++) {
      const rec = await this.fresh(await this.zoneRecord(z));
      out.push({ zone: rec.zone, defender: rec.defender, history: rec.history });
    }
    return out;
  }

  /**
   * Operator API (admin console): the zone starts over without an owner at stage 0 (as never captured); its attack
   * history is kept, the former owner keeps the counted captures (clan rating). assault.zoneChanged goes to everybody.
   */
  async resetZoneByOperator(zone: number): Promise<AssaultZone> {
    this.assertZone(zone);
    return this.d.mutex.run(zoneLockKey(zone), async () => {
      const rec = await this.zoneRecord(zone);
      rec.zone = emptyZone(zone);
      rec.defender = null;
      await this.d.storage.batch([zoneWrite(rec)]);
      this.ownerChanged(zone);
      this.d.hub.broadcast('assault.zoneChanged', { zone: rec.zone });
      this.d.logger.warn('zone reset by an operator', { zone });
      return rec.zone;
    });
  }

  /** The lock keys of every zone in their lock order (ascending zone numbers): the account deletion holds them all. */
  zoneLockKeys(): string[] {
    return Array.from({ length: this.zoneCount }, (_, i) => zoneLockKey(i + 1));
  }

  /**
   * Account deletion (./account-deletion.ts; the caller holds every zone lock, zoneLockKeys): the writes that take the
   * player out of the zones. A zone the player holds (or defends) is released as the operator's reset does
   * (resetZoneByOperator: no owner, stage 0, no defender), and the player's attacks leave every zone's history.
   * Returns the writes and the released zones, which the caller announces (assault.zoneChanged) once they are stored.
   */
  async deletionOps(playerId: PlayerId): Promise<{ ops: StorageBatchOp[]; released: AssaultZone[] }> {
    const ops: StorageBatchOp[] = [];
    const released: AssaultZone[] = [];
    for (let z = 1; z <= this.zoneCount; z++) {
      const stored = await this.d.storage.get<ZoneRecord>(C.zones, String(z));
      if (!stored) continue;
      const owned = stored.zone.ownerId === playerId || stored.defender?.playerId === playerId;
      const history = stored.history.filter((h) => h.playerId !== playerId);
      if (!owned && history.length === stored.history.length) continue;
      const rec: ZoneRecord = { ...stored, history };
      if (owned) {
        rec.zone = emptyZone(z);
        rec.defender = null;
        released.push(rec.zone);
      }
      ops.push(zoneWrite(rec));
    }
    return { ops, released };
  }

  /** Whether the player may not attack the zone: its own, or held by its clan (a fresh zone: fresh()). */
  private blocked(playerId: PlayerId, zone: AssaultZone): 'own' | 'clan' | null {
    if (zone.ownerId === playerId) return 'own';
    const clanId = this.clans.clanIdOf(playerId);
    if (clanId !== null && zone.ownerClanId === clanId) return 'clan';
    return null;
  }

  async getTargets(playerId: PlayerId, zone?: number): Promise<AssaultTarget[]> {
    await this.auth.requireAccount(playerId);
    const zones = zone === undefined ? Array.from({ length: this.zoneCount }, (_, i) => i + 1) : [zone];
    const out: AssaultTarget[] = [];
    for (const z of zones) {
      this.assertZone(z);
      const rec = await this.fresh(await this.zoneRecord(z));
      const reason = this.blocked(playerId, rec.zone);
      out.push({ zone: rec.zone, defender: rec.defender, canAttack: reason === null, blockedReason: reason, tributePerHour: this.d.config.assault.payments[z - 1] ?? 0 });
    }
    return out;
  }

  /**
   * The capture request of the client-owned levels L0/L1: the client reports an assault's outcome (optionally backed by
   * a finished battle session) and its team snapshot becomes the defender. At L2 FORBIDDEN 'use_commands'.
   */
  async report(
    playerId: PlayerId,
    p: { zone: number; outcome: 'win' | 'loss'; expectedStage: number; sessionId?: string; team?: TeamSnapshotInput },
    ctx: ConnectionContext | null,
  ): Promise<{ zone: AssaultZone; captured: boolean; serverTime: number }> {
    if (this.d.config.trustLevel === 'L2') {
      throw new BackendError('FORBIDDEN', 'The server owns the game state: a zone is captured by winning an assault battle (player.command battle.start / battle.finish)', { reason: 'use_commands' });
    }
    this.assertZone(p.zone);
    return this.d.mutex.run(zoneLockKey(p.zone), async () => {
      const a = await this.auth.requireAccount(playerId);
      const rec = await this.fresh(await this.zoneRecord(p.zone));
      const now = this.d.clock.now();
      const reason = this.blocked(playerId, rec.zone);
      if (reason) throw new BackendError('FORBIDDEN', reason === 'own' ? 'You already own this zone' : 'The zone belongs to your clan', { reason });
      if (p.sessionId !== undefined) await this.checkBattle(playerId, p.sessionId, p.zone, p.outcome);
      else if (p.outcome === 'win' && this.d.config.assault.requireVerifiedBattle) {
        throw new BackendError('VALIDATION_FAILED', 'A verified battle session is required to capture a zone', { reason: 'verified_battle_required' });
      }
      rec.history.push({ at: now, playerId, outcome: p.outcome, stage: rec.zone.stage });
      if (rec.history.length > HISTORY_KEPT) rec.history.splice(0, rec.history.length - HISTORY_KEPT);
      if (p.outcome === 'loss') {
        await this.d.storage.batch([zoneWrite(rec)]);
        return { zone: rec.zone, captured: false, serverTime: now };
      }
      if (rec.zone.stage !== p.expectedStage) {
        throw new BackendError('CONFLICT', `Zone stage is ${rec.zone.stage}, expected ${p.expectedStage}`, { reason: 'stage_changed', currentStage: rec.zone.stage });
      }
      const previousOwnerId = rec.zone.ownerId;
      rec.zone = this.capturedZone(p.zone, rec.zone.stage + 1, a, now);
      rec.defender = await this.profiles.teamOf(playerId, p.team);
      a.captures += 1;
      // The zone and the capturer's capture count (clan rating): one atomic write.
      await this.d.storage.batch([zoneWrite(rec), accountWrite(a, now)]);
      await this.captured({ playerId, zone: p.zone, stage: rec.zone.stage, previousOwnerId, at: now });
      this.d.hub.broadcast('assault.zoneChanged', { zone: rec.zone }, ctx ?? undefined);
      this.d.logger.info('zone captured', { playerId, zone: p.zone, stage: rec.zone.stage });
      return { zone: rec.zone, captured: true, serverTime: now };
    });
  }

  /** The zone after `a` captured it (its clan from the clan directory). */
  private capturedZone(zone: number, stage: number, a: AccountRecord, now: number): AssaultZone {
    const ownerClanId = this.clans.clanIdOf(a.playerId);
    return { zone, stage, ownerId: a.playerId, ownerName: a.displayName, ownerClanId, ownerClan: this.clans.brief(ownerClanId), lastCaptureAt: now };
  }

  /**
   * battle.start of an assault: the zone's stage (enemy strength) and who holds it from the player's point of view
   * (the rule op refuses the own and the own clan's zones, as the assault screen hid 'assault_fight' for them).
   */
  async zoneFacts(account: AccountRecord, zone: number): Promise<{ stage: number; owner: ZoneOwner }> {
    this.assertZone(zone);
    const rec = await this.fresh(await this.zoneRecord(zone));
    const blocked = this.blocked(account.playerId, rec.zone);
    const owner: ZoneOwner = blocked === 'own' ? 'self' : blocked === 'clan' ? 'clan' : rec.zone.ownerId ? 'other' : 'none';
    return { stage: rec.zone.stage, owner };
  }

  /**
   * Captures `zone` for the player after a won assault battle (battle.finish): the zone must still be at the stage the
   * battle started with (`expectedStage`) and must not belong to the player or the clan by now; otherwise the capture
   * is lost (the battle's rewards stay, see BattleService). Idempotent per `battleId`: a second call for the same
   * battle (a command retried after a failed write) reports what the first one did and changes nothing.
   * The zone and the capturer's account are written in one batch; with `commit` the caller writes them instead (in its
   * own batch, e.g. with the profile of battle.finish) while the zone stays locked, so nothing else changes the zone
   * between the decision and the write. For a battle already recorded `commit` gets no ops (the capture is stored).
   * After the write the capture listeners run (the clan histories; they take `clan:<id>` locks after `zone:<n>`, as
   * the lock order of services/clans.ts allows).
   */
  async captureAfterBattle(playerId: PlayerId, p: { zone: number; expectedStage: number; battleId: string }, commit?: CaptureCommit): Promise<CaptureOutcome> {
    this.assertZone(p.zone);
    return this.d.mutex.run(zoneLockKey(p.zone), async () => {
      const a = await this.auth.requireAccount(playerId);
      const rec = await this.fresh(await this.zoneRecord(p.zone));
      const seen = rec.history.find((h) => h.battleId === p.battleId && h.playerId === playerId);
      if (seen) {
        const recorded: CaptureOutcome = { captured: !seen.blocked, blocked: seen.blocked ?? null, zone: rec.zone };
        if (commit) await commit([], recorded);
        return recorded;
      }
      const now = this.d.clock.now();
      const blocked: CaptureBlocked | null = this.blocked(playerId, rec.zone) ?? (rec.zone.stage !== p.expectedStage ? 'stage_changed' : null);
      rec.history.push({ at: now, playerId, outcome: 'win', stage: rec.zone.stage, battleId: p.battleId, blocked });
      if (rec.history.length > HISTORY_KEPT) rec.history.splice(0, rec.history.length - HISTORY_KEPT);
      const write = commit ?? ((ops: StorageBatchOp[]) => this.d.storage.batch(ops));
      if (blocked) {
        const outcome: CaptureOutcome = { captured: false, blocked, zone: rec.zone };
        await write([zoneWrite(rec)], outcome);
        this.d.logger.info('zone capture lost', { playerId, zone: p.zone, expectedStage: p.expectedStage, stage: rec.zone.stage, blocked });
        return outcome;
      }
      const previousOwnerId = rec.zone.ownerId;
      rec.zone = this.capturedZone(p.zone, rec.zone.stage + 1, a, now);
      rec.defender = await this.profiles.teamOf(playerId);
      a.captures += 1;
      const outcome: CaptureOutcome = { captured: true, blocked: null, zone: rec.zone };
      await write([zoneWrite(rec), accountWrite(a, now)], outcome);
      await this.captured({ playerId, zone: p.zone, stage: rec.zone.stage, previousOwnerId, at: now });
      this.d.hub.broadcast('assault.zoneChanged', { zone: rec.zone });
      this.d.logger.info('zone captured', { playerId, zone: p.zone, stage: rec.zone.stage, battleId: p.battleId });
      return outcome;
    });
  }

  /**
   * The assault facts of the tribute.claim command (FWP.get_tribute's ASSAULT[z] and CLANS_PEOPLE_DEF): every zone's
   * owner and last capture (unix seconds, as the game kept them), and the owners who are in the player's clan now.
   */
  async tributeFacts(account: AccountRecord): Promise<TributeFacts> {
    const zones: TributeZone[] = [];
    const clan = new Set<PlayerId>();
    for (let z = 1; z <= Math.min(this.zoneCount, ASSAULT_ZONES); z++) {
      const rec = await this.fresh(await this.zoneRecord(z));
      zones.push({ zone: z, ownerId: rec.zone.ownerId, lastCaptureAt: Math.max(0, Math.floor(rec.zone.lastCaptureAt / 1000)) });
      const mine = this.clans.clanIdOf(account.playerId);
      if (mine !== null && rec.zone.ownerId && rec.zone.ownerClanId === mine) clan.add(rec.zone.ownerId);
    }
    return { zones, clanMembers: [...clan].sort() };
  }

  private async checkBattle(playerId: PlayerId, sessionId: string, zone: number, outcome: 'win' | 'loss'): Promise<void> {
    const b = await this.battles.outcomeFor(sessionId);
    if (!b) throw new BackendError('VALIDATION_FAILED', 'Battle session is unknown or not finished', { reason: 'battle_not_found', sessionId });
    if (!b.playerIds.includes(playerId)) throw new BackendError('VALIDATION_FAILED', 'Battle session belongs to another player', { reason: 'battle_not_yours', sessionId });
    if (b.mode !== 'assault' || b.zone !== zone) throw new BackendError('VALIDATION_FAILED', 'Battle session is not an assault on this zone', { reason: 'battle_mismatch', sessionId });
    const won = b.teamsOf(playerId).includes(b.winnerTeam);
    if ((outcome === 'win') !== won) throw new BackendError('VALIDATION_FAILED', 'Reported outcome does not match the battle result', { reason: 'outcome_mismatch', sessionId });
    if (outcome === 'win' && this.d.config.assault.requireVerifiedBattle && !b.verified) {
      throw new BackendError('VALIDATION_FAILED', 'Battle result is not verified', { reason: 'battle_not_verified', sessionId });
    }
  }

  /**
   * Tribute (FWP.get_tribute / get_tribute_coeff, FWP.as:21041-21088):
   *   coeff(z) = min(1, (now - lastCapture(z)) / 1 h); own zones pay ceil(payments * coeff),
   *   clanmates' zones pay ceil(payments * coeff / 10). Claimable once per hour.
   * Client-owned levels L0/L1 only (the client credits the gold; the account keeps the cooldown). At L2 the
   * tribute.claim command credits it into the canonical state with the state's own cooldown: FORBIDDEN 'use_commands'.
   */
  async claimTribute(playerId: PlayerId): Promise<TributeResult> {
    if (this.d.config.trustLevel === 'L2') {
      throw new BackendError('FORBIDDEN', 'The server owns the game state: claim the tribute with player.command tribute.claim', { reason: 'use_commands' });
    }
    return this.d.mutex.run(`tribute:${playerId}`, async () => {
      const a = await this.auth.requireAccount(playerId);
      const now = this.d.clock.now();
      if (a.nextTributeAt !== 0 && a.nextTributeAt > now) {
        throw new BackendError('INVALID_STATE', 'Tribute is not available yet', { reason: 'not_available', nextPaymentAt: a.nextTributeAt });
      }
      let myGold = 0;
      let clanGold = 0;
      const zones: TributeResult['zones'] = [];
      for (let z = 1; z <= this.zoneCount; z++) {
        const rec = await this.fresh(await this.zoneRecord(z));
        if (!rec.zone.ownerId) continue;
        const pay = this.d.config.assault.payments[z - 1] ?? 0;
        const coeff = Math.min(1, (now - rec.zone.lastCaptureAt) / this.d.config.assault.tributeIntervalMs);
        const mine = this.clans.clanIdOf(playerId);
        if (rec.zone.ownerId === playerId) {
          const g = Math.ceil(pay * coeff);
          myGold += g;
          zones.push({ zone: z, gold: g, share: 'own' });
        } else if (mine !== null && rec.zone.ownerClanId === mine) {
          const g = Math.ceil((pay * coeff) / this.d.config.assault.clanShareDivisor);
          clanGold += g;
          zones.push({ zone: z, gold: g, share: 'clan' });
        }
      }
      a.nextTributeAt = now + this.d.config.assault.tributeIntervalMs;
      await this.auth.saveAccount(a);
      return { myGold, clanGold, total: myGold + clanGold, nextPaymentAt: a.nextTributeAt, serverTime: now, zones };
    });
  }
}
